import React, { useState } from 'react';
import { MessageSquare, Send, Calendar, User, FileText, AlertTriangle, Zap, Lightbulb } from 'lucide-react';
import { OpportunityNote } from '../../types';
import UserAvatar from '../UserAvatar';

interface OpportunityNotesProps {
  notes: OpportunityNote[];
  currentUserId: string;
  currentUserName: string;
  currentUserPhoto?: string;
  onAddNote: (content: string, type: OpportunityNote['type']) => void;
}

const OpportunityNotes: React.FC<OpportunityNotesProps> = ({
  notes,
  currentUserId,
  currentUserName,
  currentUserPhoto,
  onAddNote
}) => {
  const [newNote, setNewNote] = useState('');
  const [noteType, setNoteType] = useState<OpportunityNote['type']>('general');
  const [isAdding, setIsAdding] = useState(false);

  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    setIsAdding(true);
    try {
      await onAddNote(newNote.trim(), noteType);
      setNewNote('');
      setNoteType('general');
    } catch (error) {
      console.error('Error adding note:', error);
    } finally {
      setIsAdding(false);
    }
  };

  const getNoteIcon = (type: OpportunityNote['type']) => {
    switch (type) {
      case 'strategy': return <Zap className="w-4 h-4 text-accent" />;
      case 'concern': return <AlertTriangle className="w-4 h-4 text-warning" />;
      case 'competitive': return <Lightbulb className="w-4 h-4 text-accent" />;
      default: return <MessageSquare className="w-4 h-4 text-txt-secondary" />;
    }
  };

  const getNoteColor = (type: OpportunityNote['type']) => {
    switch (type) {
      case 'strategy': return 'bg-info-soft border-l-accent';
      case 'concern': return 'bg-warning-soft border-l-warning';
      case 'competitive': return 'bg-accent-soft border-l-accent';
      default: return 'bg-bg-surface border-l-txt-muted';
    }
  };

  const sortedNotes = [...notes].sort((a, b) => 
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  return (
    <div className="space-y-4">
      {/* Add Note Form */}
      <div className="bg-bg-card/90 backdrop-blur-sm rounded-xl lg:rounded-2xl shadow-card border-0 p-4">
        <div className="flex items-start space-x-3">
          <UserAvatar 
            photoURL={currentUserPhoto}
            name={currentUserName}
            size="sm"
          />
          
          <div className="flex-1 space-y-3">
            <textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Add a note, meeting summary, or insight..."
              rows={3}
              className="w-full px-4 py-3 border-0 bg-bg-surface rounded-lg lg:rounded-xl focus:ring-2 focus:ring-accent/30 focus:bg-bg-card resize-none transition-all duration-200 text-sm"
              disabled={isAdding}
            />
            
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <select
                  value={noteType}
                  onChange={(e) => setNoteType(e.target.value as OpportunityNote['type'])}
                  className="px-3 py-1.5 text-xs border-0 bg-bg-surface rounded-lg focus:ring-2 focus:ring-accent/30"
                  disabled={isAdding}
                >
                  <option value="general">💬 General Note</option>
                  <option value="strategy">⚡ Strategy</option>
                  <option value="concern">⚠️ Concern</option>
                  <option value="competitive">💡 Competitive Intel</option>
                </select>
              </div>
              
              <button
                onClick={handleAddNote}
                disabled={!newNote.trim() || isAdding}
                className="px-4 py-2 bg-accent text-txt-inverted rounded-lg hover:bg-accent-bright disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm flex items-center space-x-2"
              >
                {isAdding ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Adding...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    <span>Add Note</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Notes Stream */}
      <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
        {sortedNotes.length === 0 ? (
          <div className="text-center py-12 bg-bg-surface rounded-xl">
            <FileText className="w-12 h-12 text-txt-muted mx-auto mb-3" />
            <p className="text-txt-tertiary font-medium">No notes yet</p>
            <p className="text-sm text-txt-muted mt-1">Add meeting notes, insights, or important details</p>
          </div>
        ) : (
          sortedNotes.map((note) => (
            <div
              key={note.id}
              className={`p-4 rounded-lg border-l-4 ${getNoteColor(note.type)} transition-all hover:shadow-card`}
            >
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  {getNoteIcon(note.type)}
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-semibold text-txt">
                        {note.createdBy === currentUserId ? 'You' : note.createdBy}
                      </span>
                      <span className="text-xs text-txt-tertiary">
                        {new Date(note.createdAt).toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                    
                    <span className="text-xs font-medium text-txt-tertiary capitalize">
                      {note.type}
                    </span>
                  </div>
                  
                  <p className="text-sm text-txt-secondary whitespace-pre-wrap leading-relaxed">
                    {note.content}
                  </p>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {sortedNotes.length > 0 && (
        <div className="text-center pt-2">
          <p className="text-xs text-txt-tertiary">
            {sortedNotes.length} note{sortedNotes.length !== 1 ? 's' : ''} • Sorted by newest first
          </p>
        </div>
      )}
    </div>
  );
};

export default OpportunityNotes;

